import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { ArrowRight, BarChart3, Bell, History, Zap, TrendingUp } from "lucide-react"
import { LanguageSwitcher } from "@/components/language-switcher"
import { useLanguage } from "@/hooks/use-language"
import { useEffect } from "react"
import { Link } from "react-router-dom"

export function Landing() {
  const { language } = useLanguage()

  // Add landing-page class for gradient background
  useEffect(() => {
    document.body.classList.add('landing-page')
    return () => {
      document.body.classList.remove('landing-page')
    }
  }, [])

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b border-border/30 backdrop-blur-md sticky top-0 z-50 bg-panel/60">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between max-w-7xl">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Zap className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold text-foreground">Pulse by Risko</span>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <Link
              to="#how-it-works"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {language === "en" ? "How it Works" : "¿Cómo funciona?"}
            </Link>
            <Link 
              to="#benefits" 
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {language === "en" ? "Benefits" : "Beneficios"}
            </Link>
          </nav>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/login">
              <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 bg-transparent">
                {language === "en" ? "Sign In" : "Iniciar Sesión"}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 md:py-32 max-w-7xl">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <Badge variant="outline" className="border-primary/30 text-primary px-4 py-1.5 text-sm">
            {language === "en" ? "Real-time Market Intelligence" : "Inteligencia de mercado en tiempo real"}
          </Badge>
          <h1 className="text-5xl md:text-7xl font-bold text-balance leading-tight">
            {language === "en" ? (
              <>Trade with <span className="gradient-text">precision</span>.</>
            ) : (
              <>Opera con <span className="gradient-text">precisión</span>.</>
            )}
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground text-balance max-w-2xl mx-auto">
            {language === "en" 
              ? "Follow the market pulse with real-time validated signals."
              : "Sigue el pulso del mercado con señales validadas en tiempo real."}
          </p>
          <div className="flex items-center justify-center gap-4 text-lg md:text-xl font-medium flex-wrap">
            <span className="text-primary flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              {language === "en" ? "Data" : "Datos"}
            </span>
            <span className="text-primary flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              {language === "en" ? "Strategy" : "Estrategia"}
            </span>
            <span className="text-secondary flex items-center gap-2">
              <Zap className="w-5 h-5" />
              {language === "en" ? "Discipline" : "Disciplina"}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
            <Button size="lg" className="gradient-button hover:gradient-button-hover text-lg px-8 h-12 shadow-lg shadow-primary/20 transition-all duration-300 font-semibold" asChild>
              <Link to="/signup">
                {language === "en" ? "7-Day Free Trial" : "Prueba Gratuita de 7 Días"}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-primary/50 text-primary hover:bg-primary/10 backdrop-blur-sm text-lg px-8 h-12 bg-card/30 transition-all duration-300"
              asChild
            >
              <Link to="/dashboard">
                {language === "en" ? "See Demo" : "Ver Demo"}
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="container mx-auto px-4 py-20 md:py-32 max-w-7xl">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-balance">
              {language === "en" ? "How it works" : "¿Cómo funciona?"}
            </h2>
            <p className="text-xl text-muted-foreground text-balance">
              {language === "en" ? "Less guessing. More precision." : "Menos suposiciones. Más precisión."}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-panel/50 backdrop-blur-md border-border/20 shadow-xl shadow-primary/10 rounded-2xl hover:shadow-2xl hover:shadow-primary/20 transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-8 space-y-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/30 shadow-lg shadow-primary/20">
                  <span className="text-2xl font-bold text-primary">1</span>
                </div>
                <h3 className="text-xl font-semibold text-foreground">
                  {language === "en" ? "Real-time Analysis" : "Análisis en tiempo real"}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {language === "en"
                    ? "We analyze the market in real time, tracking every movement and opportunity."
                    : "Analizamos el mercado en tiempo real, rastreando cada movimiento y oportunidad."}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-panel/50 backdrop-blur-md border-border/20 shadow-xl shadow-primary/10 rounded-2xl hover:shadow-2xl hover:shadow-primary/20 transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-8 space-y-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/30 shadow-lg shadow-primary/20">
                  <span className="text-2xl font-bold text-primary">2</span>
                </div>
                <h3 className="text-xl font-semibold text-foreground">
                  {language === "en" ? "Validated Signals" : "Señales validadas"}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {language === "en"
                    ? "We generate data-driven, validated trade signals based on proven setups."
                    : "Generamos señales de trading validadas basadas en datos y setups probados."}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-panel/50 backdrop-blur-md border-border/20 shadow-xl shadow-primary/10 rounded-2xl hover:shadow-2xl hover:shadow-primary/20 transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-8 space-y-4">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center border border-secondary/30 shadow-lg shadow-secondary/20">
                  <span className="text-2xl font-bold text-secondary">3</span>
                </div>
                <h3 className="text-xl font-semibold text-foreground">
                  {language === "en" ? "Instant Alerts" : "Alertas instantáneas"}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {language === "en"
                    ? "You receive alerts instantly on your dashboard, Telegram, or Discord."
                    : "Recibes alertas al instante en tu dashboard, Telegram o Discord."}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="container mx-auto px-4 py-20 md:py-32 max-w-7xl">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-balance">
              {language === "en" ? "Why Pulse?" : "¿Por qué Pulse?"}
            </h2>
            <p className="text-xl text-muted-foreground text-balance">
              {language === "en" ? "Everything you need to trade with confidence." : "Todo lo que necesitas para operar con confianza."}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-panel/50 backdrop-blur-md border-border/20 shadow-xl rounded-2xl hover:shadow-2xl hover:shadow-primary/10 transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/20 shadow-md">
                  <Zap className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {language === "en" ? "High-probability real-time signals" : "Señales de alta probabilidad en tiempo real"}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {language === "en"
                      ? "Get validated trade opportunities as they happen, backed by data and proven strategies."
                      : "Obtén oportunidades de trading validadas mientras ocurren, respaldadas por datos y estrategias probadas."}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-panel/50 backdrop-blur-md border-border/20 shadow-xl rounded-2xl hover:shadow-2xl hover:shadow-primary/10 transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/20 shadow-md">
                  <ArrowRight className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {language === "en" ? "Integration with Risko Pilot" : "Integración con Risko Pilot"}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {language === "en"
                      ? "Automatically copy signals to your trading account with one click. No manual execution needed."
                      : "Copia señales automáticamente a tu cuenta de trading con un clic. Sin ejecución manual necesaria."}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-panel/50 backdrop-blur-md border-border/20 shadow-xl rounded-2xl hover:shadow-2xl hover:shadow-primary/10 transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/20 shadow-md">
                  <History className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {language === "en" ? "Strategy and setup history" : "Historial de estrategias y setups"}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {language === "en"
                      ? "Track performance, review past signals, and learn from every trade setup."
                      : "Rastrea el rendimiento, revisa señales pasadas y aprende de cada setup de trading."}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-panel/50 backdrop-blur-md border-border/20 shadow-xl rounded-2xl hover:shadow-2xl hover:shadow-primary/10 transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/20 shadow-md">
                  <Bell className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {language === "en" ? "Direct alerts on Telegram" : "Alertas directas en Telegram"}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {language === "en"
                      ? "Never miss an opportunity. Get instant notifications wherever you are."
                      : "Nunca pierdas una oportunidad. Recibe notificaciones instantáneas donde estés."}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/20 shadow-xl rounded-3xl md:col-span-2 hover:shadow-2xl hover:shadow-primary/10 transition-all duration-300 hover:-translate-y-1" style={{ backgroundColor: 'rgba(17, 11, 33, 0.6)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/20 shadow-md">
                  <BarChart3 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {language === "en" ? "Transparent metrics and tracking" : "Métricas y seguimiento transparentes"}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {language === "en"
                      ? "Full visibility into signal performance, win rates, and strategy effectiveness. No black boxes."
                      : "Visibilidad completa del rendimiento de señales, tasas de acierto y efectividad de estrategias. Sin cajas negras."}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="container mx-auto px-4 py-20 md:py-32 max-w-7xl">
        <Card className="bg-panel/50 backdrop-blur-md border-primary/30 shadow-2xl shadow-primary/20 rounded-3xl max-w-4xl mx-auto hover:shadow-primary/30 transition-all duration-300">
          <CardContent className="p-12 md:p-16 text-center space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold text-balance leading-tight">
              {language === "en" ? (
                <>Start trading with <span className="gradient-text">data</span>, not emotions.</>
              ) : (
                <>Comienza a operar con <span className="gradient-text">datos</span>, no con emociones.</>
              )}
            </h2>
            <p className="text-xl text-muted-foreground text-balance max-w-2xl mx-auto">
              {language === "en"
                ? "Join the Risko Pulse private beta and experience precision trading."
                : "Únete a la beta privada de Risko Pulse y experimenta el trading de precisión."}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Button size="lg" className="gradient-button hover:gradient-button-hover text-lg px-10 h-14 shadow-xl shadow-primary/30 transition-all duration-300 font-semibold" asChild>
                <Link to="/signup">
                  {language === "en" ? "7-Day Free Trial" : "Prueba Gratuita de 7 Días"}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Link to="/dashboard">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-primary/50 text-primary hover:bg-primary/10 backdrop-blur-sm text-lg px-10 h-14 bg-card/30 transition-all duration-300"
                >
                  {language === "en" ? "View Dashboard" : "Ver Dashboard"}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/30 py-12">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg bg-primary flex items-center justify-center">
                <Zap className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="text-sm text-muted-foreground">
                © 2025 Pulse by Risko. {language === "en" ? "All rights reserved." : "Todos los derechos reservados."}
              </span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                {language === "en" ? "Privacy" : "Privacidad"}
              </Link>
              <Link to="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                {language === "en" ? "Terms" : "Términos"}
              </Link>
              <Link to="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                {language === "en" ? "Contact" : "Contacto"}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
